import { useState } from "react";
import { Area, AreaChart, Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFetch, Panel, Stat, PageHeader, pnlClass, Empty } from "@/components/Common";
import { fmtMoney } from "@/lib/api";
import BrokerPerformancePanel from '@/components/BrokerPerformancePanel';
import { useAuth } from '@/context/AuthContext';

const DESTS = [["", "All"], ["paper", "Paper"], ["mt5_demo", "MT5 Demo"], ["mt5_real", "MT5 Real"]];
const tt = { background: "#121826", border: "1px solid #2a3142" };

function Breakdown({ title, rows }) {
  return (
    <Panel title={title}>
      {rows?.length ? rows.map((r) => (
        <div key={r.key} className="flex justify-between border-b border-border/50 py-2 text-sm">
          <span className="font-mono">{r.key}</span><span className="text-xs text-muted-foreground">{r.trades} trades · {Math.round((r.wins / r.trades) * 100)}% win</span><span className={`num ${pnlClass(r.pnl)}`}>{fmtMoney(r.pnl)}</span>
        </div>
      )) : <Empty>No data</Empty>}
    </Panel>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [dest, setDest] = useState("paper");
  const { data } = useFetch(`/analytics${dest ? `?destination=${dest}` : ""}`);
  const s = data?.summary;
  return (
    <div className="space-y-4">
      <PageHeader title="Analytics" sub="Complete profit / loss analysis" right={
        <div className="flex rounded border">{DESTS.map(([v, l]) => <button key={l} onClick={() => setDest(v)} data-testid={`analytics-dest-${l.toLowerCase().replace(" ", "-")}`} className={`px-3 py-2 text-xs transition-colors ${dest === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>{l}</button>)}</div>
      } />
      {user?.role === 'owner' && <BrokerPerformancePanel />}
      {s && (
        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <Stat label="Net P/L (after costs)" value={fmtMoney(s.net_pnl)} tone={pnlClass(s.net_pnl)} sub={`costs ${fmtMoney(data.total_costs)}`} testid="analytics-net-pnl" />
          <Stat label="Trades" value={s.trades} sub={`${data.open_count} open`} testid="analytics-trades" />
          <Stat label="Win rate" value={`${s.win_rate}%`} />
          <Stat label="Profit factor" value={s.profit_factor ?? "—"} />
          <Stat label="Expectancy" value={fmtMoney(s.expectancy)} />
          <Stat label="Max drawdown" value={`${s.max_drawdown_pct}%`} tone="text-down" sub={s.statistically_meaningful ? "statistically meaningful sample" : `t-stat ${s.t_stat ?? "—"} · sample too small to judge`} />
        </div>
      )}
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="Equity curve">{data?.equity_curve?.length ? (
          <ResponsiveContainer width="100%" height={240}><AreaChart data={data.equity_curve}><XAxis dataKey="time" hide /><YAxis domain={["auto", "auto"]} width={70} tick={{ fill: "#94a3b8", fontSize: 11 }} /><Tooltip contentStyle={tt} /><Area dataKey="equity" stroke="#00d6a8" fill="rgba(0,214,168,0.12)" /></AreaChart></ResponsiveContainer>
        ) : <Empty>No closed trades</Empty>}</Panel>
        <Panel title="Daily P/L">{data?.daily?.length ? (
          <ResponsiveContainer width="100%" height={240}><BarChart data={data.daily}><XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} /><YAxis width={70} tick={{ fill: "#94a3b8", fontSize: 11 }} /><Tooltip contentStyle={tt} /><Bar dataKey="pnl">{data.daily.map((d) => <Cell key={d.date} fill={d.pnl >= 0 ? "#00d6a8" : "#ef4655"} />)}</Bar></BarChart></ResponsiveContainer>
        ) : <Empty>No closed trades</Empty>}</Panel>
      </div>
      <div className="grid gap-4 xl:grid-cols-4">
        <Breakdown title="By symbol" rows={data?.by_symbol} /><Breakdown title="By bot" rows={data?.by_desk} /><Breakdown title="By strategy" rows={data?.by_strategy} /><Breakdown title="By origin" rows={data?.by_origin} />
      </div>
    </div>
  );
}
